/**
 * Adaptive Training System - ENHANCED WITH METADATA
 * 
 * Uses RPE data, performance history, and exercise metadata to provide
 * intelligent, context-aware workout adjustments and recommendations
 * 
 * NEW: Level-based progression curves, mechanic-aware adjustments, force balance tracking
 */

import { getSessionHistory, getExerciseHistory, type ExerciseLog, type PTSessionLog } from './sessionStorage';
import { 
  getExerciseMetadata, 
  isCompoundMovement, 
  getExerciseLevel, 
  getForceType,
  type ExerciseMetadata 
} from './exerciseDatabase';
import { type PeriodizationPhase } from './periodization';
import { validateProgressiveOverload, getExerciseVolumeStatus } from './volumeTracking';
import { calculateRecoveryDebt } from './recoveryDebt';

export interface AdaptiveRecommendation {
  exerciseName: string;
  currentLoad: number;
  suggestedLoad: number;
  adjustment: 'increase' | 'decrease' | 'maintain';
  adjustmentPercent: number;
  reason: string;
  confidence: 'high' | 'medium' | 'low';
  basedOnSessions: number;
  // New: Specific adjustment recommendations
  adjustmentType?: 'load' | 'sets' | 'reps' | 'volume';
  suggestedSets?: number;
  suggestedReps?: number;
  currentSets?: number;
  currentReps?: number;
}

export interface WorkoutAdjustment {
  shouldAdjust: boolean;
  recommendations: AdaptiveRecommendation[];
  overallMessage: string;
}

/**
 * Get adaptive recommendations for upcoming workout based on recent performance
 * Now intelligently adjusts sets, reps, or load based on context AND periodization phase
 * SKIPS assessment exercises to preserve testing integrity
 */
export async function getAdaptiveRecommendations(
  exerciseName: string,
  currentTargetLoad: number,
  lookbackSessions: number = 3,
  recoveryScore?: number, // Consider recovery state
  periodizationPhase?: PeriodizationPhase, // NEW: Consider training phase
  isAssessment?: boolean // NEW: Skip recommendations for assessments
): Promise<AdaptiveRecommendation | null> {
  // This function IS async - renamed to force recompile
  // ===== SKIP ASSESSMENTS =====
  // Assessment exercises should not be modified by adaptive system
  if (isAssessment) {
    return null;
  }

  const exerciseHistory = await getExerciseHistory(exerciseName);
  
  if (exerciseHistory.length < 2) {
    return null; // Need at least 2 sessions of data
  }

  // ===== NEW: Get exercise metadata for context-aware recommendations =====
  const metadata = getExerciseMetadata(exerciseName);
  const exerciseLevel = metadata?.level || getExerciseLevel(exerciseName);
  const mechanic = metadata?.mechanic || (isCompoundMovement(exerciseName) ? 'compound' : 'isolation');
  const hasMetadata = metadata !== null;
  
  console.log(` Adaptive: ${exerciseName} | Level: ${exerciseLevel} | Mechanic: ${mechanic} | Metadata: ${hasMetadata ? '' : ''}`);
  
  // ===== NEW: Periodization phase awareness =====
  if (periodizationPhase) {
    console.log(` Periodization: ${periodizationPhase.name} (${periodizationPhase.type}) | Focus: ${periodizationPhase.focus} | Strategy: ${periodizationPhase.progressionStrategy}`);
  }

  // Get recent sessions (most recent first)
  const recentSessions = exerciseHistory.slice(-lookbackSessions);
  
  // Calculate average RPE (handle per-set or legacy tracking)
  const avgRPE = recentSessions.reduce((sum, ex) => {
    // Use per-set average if available, otherwise use legacy actualRPE
    const exRPE = ex.sets && ex.sets.length > 0
      ? ex.sets.reduce((setSum, set) => setSum + (set.rpe || 0), 0) / ex.sets.filter(s => s.rpe !== null).length
      : ex.actualRPE || 7;
    return sum + exRPE;
  }, 0) / recentSessions.length;
  
  // Get target RPE (parse from string like "7-8 RPE")
  const targetRPE = parseRPE(recentSessions[0]?.targetRPE || '7.5');
  
  // Calculate RPE delta
  const rpeDelta = avgRPE - targetRPE;
  
  // Determine adjustment
  let adjustment: 'increase' | 'decrease' | 'maintain' = 'maintain';
  let adjustmentPercent = 0;
  let reason = '';
  let confidence: 'high' | 'medium' | 'low' = 'medium';
  
  // ===== ENHANCED: Metadata-aware progression curves =====
  // Define adjustment magnitudes based on exercise metadata
  const getAdjustmentMagnitudes = () => {
    // Level-based progression curves
    const levelFactors = {
      'beginner': { small: 2.5, large: 5 },     // Gentler for beginners
      'intermediate': { small: 5, large: 10 },  // Standard progression
      'expert': { small: 2.5, large: 5 }        // Micro-progressions for advanced
    };
    
    // Mechanic-based adjustments
    const mechanicFactors = {
      'compound': 0.85,   // 15% more conservative (CNS intensive)
      'isolation': 1.0    // Full progression rate
    };
    
    const levelFactor = levelFactors[exerciseLevel];
    const mechanicFactor = mechanicFactors[mechanic];
    
    return {
      small: levelFactor.small * mechanicFactor,
      large: levelFactor.large * mechanicFactor
    };
  };
  
  const adjustmentMagnitudes = getAdjustmentMagnitudes();
  
  // Logic: RPE-based progressive overload WITH metadata awareness
  if (avgRPE < 6.0 && targetRPE >= 7) {
    // Too easy - increase load
    adjustment = 'increase';
    adjustmentPercent = avgRPE < 5 ? adjustmentMagnitudes.large : adjustmentMagnitudes.small;
    reason = `Avg RPE ${avgRPE.toFixed(1)} too low (target: ${targetRPE}). ${mechanic === 'compound' ? 'Compound lift - conservative increase' : 'Ready to progress'}.`;
    confidence = recentSessions.length >= 3 ? 'high' : 'medium';
  } else if (avgRPE >= 9.0) {
    // Too hard - decrease load
    adjustment = 'decrease';
    adjustmentPercent = avgRPE >= 9.5 ? -adjustmentMagnitudes.large : -adjustmentMagnitudes.small;
    reason = `Avg RPE ${avgRPE.toFixed(1)} too high. Reduce to prevent overtraining${exerciseLevel === 'expert' ? ' (advanced exercise)' : ''}.`;
    confidence = 'high';
  } else if (avgRPE >= 8.5 && targetRPE <= 8) {
    // Slightly too hard
    adjustment = 'decrease';
    adjustmentPercent = -adjustmentMagnitudes.small;
    reason = `RPE ${avgRPE.toFixed(1)} slightly high. ${mechanic === 'compound' ? 'Compound - reduce for recovery' : 'Small reduction recommended'}.`;
    confidence = 'medium';
  } else if (avgRPE <= 6.5 && targetRPE >= 7.5) {
    // Slightly too easy
    adjustment = 'increase';
    adjustmentPercent = adjustmentMagnitudes.small;
    reason = `RPE ${avgRPE.toFixed(1)} slightly low. ${exerciseLevel === 'beginner' ? 'Steady progression for skill building' : 'Room for progression'}.`;
    confidence = 'medium';
  } else {
    // On target
    adjustment = 'maintain';
    adjustmentPercent = 0;
    reason = `RPE ${avgRPE.toFixed(1)} on target (goal: ${targetRPE}). ${mechanic === 'compound' ? 'Compound - maintain for consistency' : 'Maintain current load'}.`;
    confidence = 'high';
  }
  
  // ===== PERIODIZATION OVERRIDE: Phase-specific logic takes priority =====
  if (periodizationPhase) {
    const phaseRPE = avgRPE || targetRPE; // Use actual or target
    
    // DELOAD PHASE: Force reduction regardless of RPE
    if (periodizationPhase.type === 'deload') {
      adjustment = 'maintain';
      adjustmentPercent = -30; // Drop to 70% of current
      reason = `${periodizationPhase.name}: Recovery week - reducing load to 70% for active recovery`;
      confidence = 'high';
    }
    
    // REALIZATION/TESTING PHASE: Maintain, no progression
    else if (periodizationPhase.type === 'realization') {
      adjustment = 'maintain';
      adjustmentPercent = 0;
      reason = `${periodizationPhase.name}: Testing week - maintain current loads, focus on execution`;
      confidence = 'high';
    }
    
    // ACCUMULATION PHASE: Prefer volume over intensity
    else if (periodizationPhase.type === 'accumulation') {
      if (phaseRPE < periodizationPhase.rpeTarget && adjustment === 'increase') {
        // Instead of adding load, we'll flag to add sets in the strategy function
        adjustment = 'increase';
        adjustmentPercent = 0; // Don't change load
        reason = `${periodizationPhase.name}: RPE ${phaseRPE.toFixed(1)} < target ${periodizationPhase.rpeTarget} - add sets, not load`;
        confidence = 'high';
      } else if (phaseRPE > periodizationPhase.rpeTarget + 1) {
        // RPE too high for accumulation phase
        adjustment = 'decrease';
        adjustmentPercent = -adjustmentMagnitudes.small;
        reason = `${periodizationPhase.name}: RPE ${phaseRPE.toFixed(1)} too high for volume phase - reduce intensity`;
        confidence = 'high';
      }
    }
    
    // INTENSIFICATION PHASE: Prefer intensity over volume
    else if (periodizationPhase.type === 'intensification') {
      if (phaseRPE < periodizationPhase.rpeTarget && adjustment === 'increase') {
        // Add load but reduce volume
        adjustment = 'increase';
        adjustmentPercent = adjustmentMagnitudes.small;
        reason = `${periodizationPhase.name}: RPE ${phaseRPE.toFixed(1)} < target ${periodizationPhase.rpeTarget} - increasing intensity, will reduce sets`;
        confidence = 'high';
      } else if (phaseRPE > periodizationPhase.rpeTarget + 0.5) {
        // Be more conservative in intensification
        adjustment = 'decrease';
        adjustmentPercent = -adjustmentMagnitudes.small;
        reason = `${periodizationPhase.name}: RPE ${phaseRPE.toFixed(1)} exceeds target ${periodizationPhase.rpeTarget} - reduce for recovery`;
        confidence = 'high';
      }
    }
  }
  
  // Check for consistent performance decline
  if (recentSessions.length >= 3) {
    const performanceTrend = analyzePerformanceTrend(recentSessions);
    if (performanceTrend === 'declining' && adjustment !== 'decrease') {
      adjustment = 'decrease';
      adjustmentPercent = -10;
      reason = 'Performance declining over last 3 sessions. Deload recommended.';
      confidence = 'high';
    } else if (performanceTrend === 'improving' && adjustment === 'maintain') {
      adjustment = 'increase';
      adjustmentPercent = 5;
      reason = 'Performance improving consistently. Ready for more load.';
      confidence = 'high';
    }
  }
  
  // Extract current sets and reps from most recent session
  const lastSession = recentSessions[recentSessions.length - 1];
  const currentSetsReps = parseTargetSetsReps(lastSession.targetSets);
  
  // Determine HOW to adjust (load vs sets vs reps) based on context
  const adjustmentStrategy = await determineAdjustmentStrategy(
    adjustment,
    adjustmentPercent,
    avgRPE,
    targetRPE,
    currentSetsReps,
    exerciseName,
    recoveryScore,
    periodizationPhase // NEW: Pass periodization phase
  );
  
  return {
    exerciseName,
    currentLoad: currentTargetLoad,
    suggestedLoad: adjustmentStrategy.suggestedLoad,
    adjustment,
    adjustmentPercent,
    reason: adjustmentStrategy.reason,
    confidence,
    basedOnSessions: recentSessions.length,
    adjustmentType: adjustmentStrategy.adjustmentType,
    suggestedSets: adjustmentStrategy.suggestedSets,
    suggestedReps: adjustmentStrategy.suggestedReps,
    currentSets: currentSetsReps.sets,
    currentReps: currentSetsReps.reps,
  };
}

/**
 * Parse sets and reps from target string (e.g., "3x5" or "4 × 8")
 */
function parseTargetSetsReps(targetSets: string): { sets: number; reps: number } {
  const match = targetSets.match(/(\d+)\s*[x×]\s*(\d+)/i);
  if (match) {
    return {
      sets: parseInt(match[1]),
      reps: parseInt(match[2]),
    };
  }
  return { sets: 3, reps: 8 }; // Default fallback
}

/**
 * Intelligently determine HOW to adjust training (load, sets, or reps)
 * Based on RPE, recovery, exercise type, training context, AND periodization phase
 */
async function determineAdjustmentStrategy(
  adjustment: 'increase' | 'decrease' | 'maintain',
  adjustmentPercent: number,
  avgRPE: number,
  targetRPE: number,
  currentSetsReps: { sets: number; reps: number },
  exerciseName: string,
  recoveryScore?: number,
  periodizationPhase?: PeriodizationPhase // NEW
): Promise<{
  suggestedLoad: number;
  suggestedSets?: number;
  suggestedReps?: number;
  adjustmentType: 'load' | 'sets' | 'reps' | 'volume';
  reason: string;
}> {
  const { sets: currentSets, reps: currentReps } = currentSetsReps;
  
  // ===== NEW: Use metadata for smarter decisions =====
  const metadata = getExerciseMetadata(exerciseName);
  const mechanic = metadata?.mechanic || (isCompoundMovement(exerciseName) ? 'compound' : 'isolation');
  const exerciseLevel = metadata?.level || getExerciseLevel(exerciseName);
  
  const isCompound = mechanic === 'compound';
  const isAccessory = mechanic === 'isolation';
  const poorRecovery = recoveryScore !== undefined && recoveryScore < 70;
  const excellentRecovery = recoveryScore !== undefined && recoveryScore >= 85;
  
  // ===== NEW: VOLUME TRACKING CHECK =====
  // Check if volume is stagnant for this exercise
  try {
    const sessions = await getSessionHistory();
    const volumeStatus = validateProgressiveOverload(exerciseName, sessions);
    
    // If volume is stagnant for 3+ weeks and RPE is comfortable, suggest adding sets
    if (volumeStatus.weeksStagnant >= 3 && avgRPE < targetRPE && adjustment !== 'decrease') {
      console.log(` Volume Alert: ${exerciseName} stagnant for ${volumeStatus.weeksStagnant} weeks`);
      
      return {
        suggestedLoad: 0, // Keep load the same
        suggestedSets: Math.min(currentSets + 1, 6), // Add 1 set, cap at 6
        suggestedReps: currentReps,
        adjustmentType: 'sets',
        reason: `Volume stagnant for ${volumeStatus.weeksStagnant} weeks (RPE ${avgRPE.toFixed(1)}). ${volumeStatus.recommendation}`
      };
    }
  } catch (error) {
    console.error('Error checking volume status:', error);
    // Continue with normal logic if volume check fails
  }
  
  // ===== PERIODIZATION PHASE STRATEGY =====
  // Phase dictates HOW we progress (sets vs load vs reps)
  if (periodizationPhase) {
    const strategy = periodizationPhase.progressionStrategy;
    
    // ACCUMULATION: Add sets (build volume)
    if (strategy === 'add-sets' && adjustment === 'increase' && adjustmentPercent === 0) {
      return {
        suggestedLoad: 0, // Placeholder, will be calculated below
        suggestedSets: Math.min(currentSets + 1, 6), // Cap at 6 sets
        suggestedReps: currentReps,
        adjustmentType: 'sets',
        reason: `${periodizationPhase.name}: Adding 1 set (${currentSets} → ${currentSets + 1})`
      };
    }
    
    // INTENSIFICATION: Add load, reduce sets
    if (strategy === 'add-load' && adjustment === 'increase') {
      return {
        suggestedLoad: 0, // Will be calculated with percentage
        suggestedSets: currentSets > 3 ? currentSets - 1 : currentSets, // Drop a set if >3
        suggestedReps: currentReps,
        adjustmentType: 'load',
        reason: `${periodizationPhase.name}: Increasing load, ${currentSets > 3 ? 'reducing volume' : 'maintaining volume'}`
      };
    }
    
    // DELOAD/REALIZATION: Maintain
    if (strategy === 'maintain') {
      return {
        suggestedLoad: 0, // Will keep current or reduce
        suggestedSets: currentSets,
        suggestedReps: currentReps,
        adjustmentType: 'load',
        reason: periodizationPhase.name
      };
    }
  }
  
  // MAINTAIN - no changes needed
  if (adjustment === 'maintain') {
    return {
      suggestedLoad: 0, // Will be calculated elsewhere
      adjustmentType: 'load',
      reason: `RPE ${avgRPE.toFixed(1)} on target. Continue as programmed.`,
    };
  }
  
  // DECREASE ADJUSTMENTS
  if (adjustment === 'decrease') {
    // Poor recovery or high RPE → reduce volume (sets) first, preserve intensity
    if (poorRecovery || avgRPE >= 9.0) {
      const newSets = Math.max(1, currentSets - 1);
      return {
        suggestedLoad: 0,
        suggestedSets: newSets,
        suggestedReps: currentReps,
        adjustmentType: 'sets',
        reason: `${poorRecovery ? 'Poor recovery' : 'High RPE'} - reduce volume by 1 set. Maintain intensity.`,
      };
    }
    
    // Moderate fatigue → small load reduction
    return {
      suggestedLoad: 0, // Will be calculated with adjustmentPercent
      adjustmentType: 'load',
      reason: `Reduce load ${Math.abs(adjustmentPercent)}% to manage fatigue.`,
    };
  }
  
  // INCREASE ADJUSTMENTS
  if (adjustment === 'increase') {
    const veryEasy = avgRPE < 5.5;
    
    // Excellent recovery + very easy → add volume (sets or reps)
    if (excellentRecovery && veryEasy) {
      if (isCompound && currentSets < 5) {
        // Compounds: add sets (better for strength)
        return {
          suggestedLoad: 0,
          suggestedSets: currentSets + 1,
          suggestedReps: currentReps,
          adjustmentType: 'sets',
          reason: `Excellent recovery + easy RPE - add 1 set for more volume.`,
        };
      } else if (isAccessory && currentReps < 15) {
        // Accessories: add reps (better for hypertrophy)
        return {
          suggestedLoad: 0,
          suggestedSets: currentSets,
          suggestedReps: currentReps + 2,
          adjustmentType: 'reps',
          reason: `Excellent recovery + easy RPE - add 2 reps for hypertrophy stimulus.`,
        };
      }
    }
    
    // Good recovery + moderately easy → increase load (progressive overload)
    if (avgRPE < 6.5 && (!recoveryScore || recoveryScore >= 70)) {
      return {
        suggestedLoad: 0, // Will be calculated with adjustmentPercent
        adjustmentType: 'load',
        reason: `RPE low - increase load ${adjustmentPercent}% for progressive overload.`,
      };
    }
    
    // Default: increase load
    return {
      suggestedLoad: 0,
      adjustmentType: 'load',
      reason: `Ready for progression - increase load ${adjustmentPercent}%.`,
    };
  }
  
  // Fallback
  return {
    suggestedLoad: 0,
    adjustmentType: 'load',
    reason: 'Continue as programmed.',
  };
}

// NOTE: isCompoundExercise function removed - use isCompoundMovement from exerciseDatabase directly

/**
 * Analyze performance trend over recent sessions
 */
function analyzePerformanceTrend(sessions: ExerciseLog[]): 'improving' | 'stable' | 'declining' {
  if (sessions.length < 3) return 'stable';
  
  // Compare first half vs second half of sessions
  const midpoint = Math.floor(sessions.length / 2);
  const firstHalf = sessions.slice(0, midpoint);
  const secondHalf = sessions.slice(midpoint);
  
  const firstAvgRPE = firstHalf.reduce((sum, ex) => {
    const exRPE = ex.sets && ex.sets.length > 0
      ? ex.sets.reduce((setSum, set) => setSum + (set.rpe || 0), 0) / ex.sets.filter(s => s.rpe !== null).length
      : ex.actualRPE || 7;
    return sum + exRPE;
  }, 0) / firstHalf.length;
  
  const secondAvgRPE = secondHalf.reduce((sum, ex) => {
    const exRPE = ex.sets && ex.sets.length > 0
      ? ex.sets.reduce((setSum, set) => setSum + (set.rpe || 0), 0) / ex.sets.filter(s => s.rpe !== null).length
      : ex.actualRPE || 7;
    return sum + exRPE;
  }, 0) / secondHalf.length;
  
  // Also check load progression (handle per-set or legacy)
  const firstAvgLoad = firstHalf.reduce((sum, ex) => {
    const exLoad = ex.sets && ex.sets.length > 0
      ? ex.sets.reduce((setSum, set) => setSum + set.weight, 0) / ex.sets.length
      : ex.actualLoad || 0;
    return sum + exLoad;
  }, 0) / firstHalf.length;
  
  const secondAvgLoad = secondHalf.reduce((sum, ex) => {
    const exLoad = ex.sets && ex.sets.length > 0
      ? ex.sets.reduce((setSum, set) => setSum + set.weight, 0) / ex.sets.length
      : ex.actualLoad || 0;
    return sum + exLoad;
  }, 0) / secondHalf.length;
  
  const rpeChange = secondAvgRPE - firstAvgRPE;
  const loadChange = secondAvgLoad - firstAvgLoad;
  
  // If load increased but RPE stayed same or decreased = improving
  if (loadChange > 0 && rpeChange <= 0.5) return 'improving';
  
  // If RPE increased significantly without load increase = declining
  if (rpeChange > 1.0 && loadChange <= 0) return 'declining';
  
  // If load decreased = likely declining
  if (loadChange < 0) return 'declining';
  
  return 'stable';
}

/**
 * Parse RPE string to number (handles ranges like "7-8 RPE")
 */
function parseRPE(rpeStr: string): number {
  if (!rpeStr || rpeStr === 'nan') return 7.5;
  
  // Handle ranges like "7-8"
  const rangeMatch = rpeStr.match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)/);
  if (rangeMatch) {
    const lower = parseFloat(rangeMatch[1]);
    const upper = parseFloat(rangeMatch[2]);
    return (lower + upper) / 2;
  }
  
  // Single number
  const match = rpeStr.match(/(\d+(?:\.\d+)?)/);
  if (!match) return 7.5;
  
  return parseFloat(match[1]);
}


/**
 * Get smart recommendations summary for dashboard
 */
export async function getSmartRecommendationsSummary(): Promise<{
  hasRecommendations: boolean;
  summary: string;
  actionItems: string[];
}> {
  const sessions = await getSessionHistory();
  
  if (sessions.length < 2) {
    return {
      hasRecommendations: false,
      summary: 'Complete a few workouts to get personalized recommendations',
      actionItems: []
    };
  }
  
  const actionItems: string[] = [];
  
  // Check recent session for specific recommendations
  const lastSession = sessions[sessions.length - 1];
  if (lastSession.sessionType !== 'range' && lastSession.exercises) {
    for (const ex of lastSession.exercises) {
      if (ex.actualLoad && ex.targetLoad) {
        const rec = await getAdaptiveRecommendations(ex.exerciseName, ex.targetLoad, 3, lastSession.readiness);
        if (rec && rec.adjustment !== 'maintain' && rec.confidence === 'high') {
        const icon = rec.adjustment === 'increase' ? '' : '';
        const adjustmentDetail = rec.adjustmentType === 'sets' 
          ? ` (${rec.suggestedSets} sets)`
          : rec.adjustmentType === 'reps'
          ? ` (${rec.suggestedReps} reps)`
          : '';
        actionItems.push(`${icon} ${ex.exerciseName}: ${rec.reason}${adjustmentDetail}`);
        }
      }
    }
  }
  
  if (actionItems.length > 0) {
    return {
      hasRecommendations: true,
      summary: `${actionItems.length} adjustment(s) recommended based on your recent performance`,
      actionItems: actionItems.slice(0, 3) // Top 3
    };
  }
  
  return {
    hasRecommendations: false,
    summary: 'All exercises progressing well. Continue as programmed.',
    actionItems: []
  };
}
 
